import { Component, effect, inject, signal, viewChild } from '@angular/core';

import {
  NotificationService,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiRecordDatasource as OuiDatasource,
  OuiFileDropZoneComponent,
  OuiRowComponent,
  OuiStackComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';

import { CardComponent } from '../../../components/card/card.component';
import { LogTableComponent } from '../../../components/log-table/log-table.component';
import { PageComponent } from '../../../components/page/page';
import { ValidationErrorsDialogComponent } from '../../../components/validation-errors-dialog/validation-errors-dialog.component';
import { WorkflowLogEntry } from '../../../models/workflow-history.model';
import { BuyerManagementService } from '../../../services/buyer-management/buyer-management.service';
import { BasePageComponent } from '../../base/base.component';

@Component({
  selector: 'app-buyer-login-upload',
  standalone: true,
  imports: [
    OuiStackComponent,
    OuiRowComponent,
    OuiTextComponent,
    OuiButtonComponent,
    OuiFileDropZoneComponent,
    CardComponent,
    PageComponent,
    LogTableComponent,
    ValidationErrorsDialogComponent,
    OuiBoxComponent,
    OuiCenterComponent,
  ],
  templateUrl: './buyer-login-upload.component.html',
})
export class BuyerLoginUploadPage extends BasePageComponent {
  protected readonly buyerService = inject(BuyerManagementService);

  protected readonly selectedFile = signal<File | undefined>(undefined);
  protected readonly fileDropZone = viewChild<OuiFileDropZoneComponent>('fileDropZone');
  protected readonly dataSyncHistoryDatasource =
    signal<OuiDatasource<WorkflowLogEntry> | undefined>(undefined);
  protected readonly validationErrors = signal<string[] | undefined>(undefined);
  private readonly notification = inject(NotificationService);

  constructor() {
    super();

    this.buyerService.buyerLoginImportHistory.reload();
    effect(() => {
      const data = this.buyerService.buyerLoginImportHistory.value();
      if (!data) return;
      this.dataSyncHistoryDatasource.set(new OuiDatasource(data));
    });
  }

  protected handleFileAttached(file: File | undefined) {
    if (file) this.selectedFile.set(file);
  }

  protected uploadFile() {
    const file = this.selectedFile();
    if (!file) {
      this.notification.showAlert({
        message: 'Please select a file to upload.',
        variant: 'warn',
        symbolName: 'error',
      });
      return;
    }

    this.buyerService.uploadBuyerLogins(file).subscribe({
      next: () => {
        this.fileDropZone()?.selectedFile.set(undefined);
        this.selectedFile.set(undefined);
      },
      error: (err) => {
        const { errors } = err.error as { errors?: string[] };
        if (errors) {
          this.displayValidationErrors(errors);
        } else {
          const errorMessage =
            err.error || 'Failed to upload the file. Please try again.';
          this.notification.showAlert({
            message: errorMessage,
            variant: 'warn',
            symbolName: 'error',
          });
        }
      },
      complete: () => {
        this.notification.showSnackbar({
          message: 'Buyer Login Upload Successful',
          variant: 'success',
          symbolName: 'check',
        });
      },
    });
  }

  protected displayValidationErrors(errors: string[]) {
    this.validationErrors.set(errors);
  }
}
